import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Briefcase, Plus, ExternalLink, Lock, Monitor, Phone, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';

const LINK_TYPES = [
  { value: 'crm', label: 'CRM System', icon: Globe },
  { value: 'dialer', label: 'Dialer', icon: Phone },
  { value: 'vdi', label: 'Virtual Desktop (VDI)', icon: Monitor }
];

export default function ClientBridgePage() {
  const { api, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  const [linkData, setLinkData] = useState({
    name: '',
    url: '',
    link_type: '',
    description: ''
  });

  const checkClockStatus = useCallback(async () => {
    try {
      const response = await api.get('/time/current-status');
      setClockedIn(response.data.clocked_in);
    } catch (error) {
      console.error('Failed to check clock status:', error);
    }
  }, [api]);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/client-bridge/links');
      setLinks(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not clocked in
        setLinks([]);
      } else {
        toast.error('Failed to fetch links');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    checkClockStatus();
    if (user?.role === 'client' || user?.role === 'admin') {
      fetchLinks();
    }
  }, [checkClockStatus, fetchLinks, user?.role]);

  useEffect(() => {
    if (clockedIn || user?.role === 'client' || user?.role === 'admin') {
      fetchLinks();
    }
  }, [clockedIn, fetchLinks, user?.role]);

  const handleAddLink = async () => {
    if (!linkData.name || !linkData.url || !linkData.link_type) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.post('/client-bridge/links', linkData);
      toast.success('Link added successfully');
      setShowAddModal(false);
      setLinkData({ name: '', url: '', link_type: '', description: '' });
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add link');
    }
  };

  const getLinkTypeInfo = (type) => {
    return LINK_TYPES.find(lt => lt.value === type) || LINK_TYPES[0];
  };

  const isClientOrAdmin = user?.role === 'client' || user?.role === 'admin';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-bridge-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Client <span className="text-[#D4AF37]">Bridge</span>
            </h1>
            <p className="text-white/50 mt-1">Secure workspace access portal</p>
          </div>
          {isClientOrAdmin && (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#D4AF37] text-[#0A091E] hover:bg-[#D4AF37]/80"
              data-testid="btn-add-link"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Link
            </Button>
          )}
        </div>

        {/* Security Notice for Agents */}
        {!isClientOrAdmin && !clockedIn && (
          <Alert className="bg-[#F59E0B]/10 border-[#F59E0B]/30">
            <Lock className="w-4 h-4 text-[#F59E0B]" />
            <AlertDescription className="text-white/70">
              Client Bridge access requires an active shift. Please clock in to access client resources.
            </AlertDescription>
          </Alert>
        )}

        {/* Security Info Card */}
        <Card className="glass-card border-0 border-l-4 border-l-[#D4AF37]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">AES-256 Encrypted Access</h3>
                <p className="text-white/50 text-sm">
                  All Client Bridge links are encrypted using AES-256 encryption. Access is logged in the 
                  immutable audit trail for ISO/GDPR compliance. Links are only accessible during active shifts.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Links Grid */}
        {(clockedIn || isClientOrAdmin) && (
          <>
            {loading ? (
              <div className="text-center py-12 text-white/50">Loading secure links...</div>
            ) : links.length === 0 ? (
              <Card className="glass-card border-0">
                <CardContent className="py-12 text-center">
                  <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No client links configured</p>
                  {isClientOrAdmin && (
                    <Button
                      onClick={() => setShowAddModal(true)}
                      className="mt-4 bg-[#D4AF37] text-[#0A091E]"
                    >
                      Add Your First Link
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {links.map((link) => {
                  const typeInfo = getLinkTypeInfo(link.link_type);
                  return (
                    <Card 
                      key={link.id} 
                      className="glass-card border-0 group hover:border-[#D4AF37]/50"
                      data-testid={`link-card-${link.id}`}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-xl bg-[#1F1B71] flex items-center justify-center group-hover:bg-[#D4AF37]/20">
                            <typeInfo.icon className="w-6 h-6 text-[#D4AF37]" />
                          </div>
                          <Badge className="bg-[#4B167A]/40 text-white">
                            {typeInfo.label}
                          </Badge>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-white mb-2">{link.name}</h3>
                        {link.description && (
                          <p className="text-white/50 text-sm mb-4 line-clamp-2">{link.description}</p>
                        )}
                        
                        <Button
                          onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                          className="w-full bg-[#1F1B71] hover:bg-[#D4AF37] hover:text-[#0A091E] text-white border border-[#D4AF37]/30"
                          data-testid={`btn-open-${link.id}`}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Launch Secure Access
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* SLA Progress for Clients */}
        {user?.role === 'client' && (
          <Card className="glass-card border-0">
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-white">SLA Progress Overview</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#D4AF37] font-mono">98.5%</p>
                  <p className="text-white/50 text-sm mt-1">Uptime SLA</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#10B981] font-mono">94.2%</p>
                  <p className="text-white/50 text-sm mt-1">Quality Score</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white font-mono">2:45</p>
                  <p className="text-white/50 text-sm mt-1">Avg Handle Time</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#4B167A] font-mono">1,247</p>
                  <p className="text-white/50 text-sm mt-1">Calls This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Link Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#D4AF37]" />
              Add Client Bridge Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Link Name</Label>
              <Input
                value={linkData.name}
                onChange={(e) => setLinkData({ ...linkData, name: e.target.value })}
                placeholder="e.g., Salesforce CRM"
                className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                data-testid="input-link-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">URL</Label>
              <Input
                value={linkData.url}
                onChange={(e) => setLinkData({ ...linkData, url: e.target.value })}
                placeholder="https://..."
                className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                data-testid="input-link-url"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Link Type</Label>
              <Select
                value={linkData.link_type}
                onValueChange={(value) => setLinkData({ ...linkData, link_type: value })}
              >
                <SelectTrigger className="bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-link-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  {LINK_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Description (Optional)</Label>
              <Textarea
                value={linkData.description}
                onChange={(e) => setLinkData({ ...linkData, description: e.target.value })}
                placeholder="Brief description..."
                className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                data-testid="input-link-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-white/70">
              Cancel
            </Button>
            <Button onClick={handleAddLink} className="bg-[#D4AF37] text-[#0A091E]" data-testid="btn-submit-link">
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
